import {
  ComponentProps,
  memo,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select as AntSelect,
  SelectProps as AntSelectProps,
  Skeleton
} from 'antd';
import cc from 'classcat';
import { SELECT_PROVIDERS_TYPE } from 'utils/casino/casino';
import { EIconSize } from 'utils/constants/app/ui';
import { isMobile } from 'utils/is-mobile';
import { GlobalIcon } from 'components/GlobalIcon';
import { Button } from 'newcomponents/UI/Button';
import type { DefaultOptionType, RefSelectProps } from 'antd/lib/select/index';
import './index.less';

const SHOW_SEARCH_MAX_LENGTH = 5;
export const Option = AntSelect.Option;

type SelectProps = AntSelectProps & {
  label?: string;
  wrapperClassName?: string;
  withoutMargin?: boolean;
  disabled?: boolean;
  children?: ReactElement[] | null;
  search?: boolean;
  onApply?: MouseEventHandler<HTMLElement>;
  openDirectionBottom?: boolean;
  customButton?: ReactNode;
  type?: string;
  applayButtonDisable?: boolean;
  hideSuffixIcon?: boolean;
  skipOneValueDisabled?: boolean;
};

type TOpenDirection = ComponentProps<typeof AntSelect>['placement'];

const ANTD_DROPDOWN_SCROLL_AREA_SELECTOR = '.rc-virtual-list-holder';

export const Select = memo(
  ({
    label,
    wrapperClassName,
    withoutMargin,
    disabled,
    children,
    search = true,
    onSelect,
    onApply,
    openDirectionBottom,
    customButton,
    onDropdownVisibleChange: extraOnDropdownVisibleChange,
    applayButtonDisable,
    hideSuffixIcon,
    skipOneValueDisabled,
    ...props
  }: SelectProps): ReactElement => {
    const dropdownId = useId();
    const selectRef = useRef<RefSelectProps | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(props.open || false);
    const xSelectWrapperRef = useRef<HTMLDivElement | null>(null);
    const [openDirection, setOpenDirection] =
      useState<TOpenDirection>('bottomLeft');

    const childrenArr = Array.isArray(children) ? children : [children];
    const { t } = useTranslation();

    const checkSelectType = useMemo(
      () => ({
        providers: props.type === SELECT_PROVIDERS_TYPE
      }),
      [props.type]
    );

    const handleSelect = useCallback(
      (value: any, option: DefaultOptionType) => {
        if (props.mode !== 'multiple' && search && selectRef.current) {
          selectRef.current.blur();
        }

        if (onSelect) {
          onSelect(value, option);
        }
      },
      [search, onSelect]
    );

    const isDisabled =
      !!(
        typeof disabled === 'undefined' &&
        children?.length === 1 &&
        !skipOneValueDisabled
      ) || disabled;

    const getPlacement = (open: boolean) => {
      setIsDropdownOpen(open);
      extraOnDropdownVisibleChange?.(open);

      if (xSelectWrapperRef.current) {
        const elemFromTop =
          xSelectWrapperRef.current?.getBoundingClientRect().bottom;

        if (elemFromTop > window.innerHeight - elemFromTop) {
          setOpenDirection('topLeft');

          return;
        }
      }

      setOpenDirection('bottomLeft');
    };

    const isLoadingProvider =
      checkSelectType.providers &&
      (!(props.value || []).length || props.loading);

    // this effect need to improve scrolling behavior on mobile devices in case of page has scroll to
    useEffect(() => {
      if (isDropdownOpen) {
        const virtualListContainer =
          document.getElementById(dropdownId)?.parentNode;

        if (virtualListContainer) {
          const holder = virtualListContainer.querySelector(
            ANTD_DROPDOWN_SCROLL_AREA_SELECTOR
          );

          if (holder) {
            const handler: EventListener = e => {
              e.stopPropagation();
            };

            holder.addEventListener('touchmove', handler);

            return () => {
              holder.removeEventListener('touchmove', handler);
            };
          }
        }
      }
    }, [isDropdownOpen]);

    const shouldShowLoadingIcon =
      hideSuffixIcon && props.loading && !isDropdownOpen;

    return (
      <div
        className={cc([
          'x-select',
          { 'x-select__withoutMargin': withoutMargin },
          wrapperClassName
        ])}
        ref={xSelectWrapperRef}
      >
        {!!label && <div className="x-select__label">{label}</div>}
        <AntSelect
          ref={selectRef}
          disabled={isDisabled}
          placement={!openDirectionBottom ? openDirection : undefined}
          {...(!shouldShowLoadingIcon && {
            suffixIcon: (
              <>
                {isLoadingProvider ? (
                  <Skeleton
                    title={{ style: { width: 81, height: 28, margin: 0 } }}
                    paragraph={false}
                    active
                  />
                ) : (
                  <span style={{ width: '100%' }} />
                )}

                <GlobalIcon
                  lib="generic"
                  name="caretDownSmall"
                  theme="default"
                  size={EIconSize._20}
                />
              </>
            )
          })}
          onDropdownVisibleChange={getPlacement}
          showAction={['focus']}
          showSearch={
            search && (childrenArr?.length ?? 0) > SHOW_SEARCH_MAX_LENGTH
          }
          filterOption={(input, option) => {
            return `${option?.children ?? ''}`
              .toLowerCase()
              .includes(input.toLowerCase());
          }}
          onSelect={handleSelect}
          dropdownRender={item => {
            return onApply ? (
              <div
                id={dropdownId}
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {item}
                <div className="v3Select__overridden__buttonWrapper">
                  {customButton || (
                    <Button
                      type="primary"
                      className="v3Select__overridden"
                      onClick={onApply}
                      disabled={applayButtonDisable}
                    >
                      {t('account.applyButton')}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div id={dropdownId}>{item}</div>
            );
          }}
          {...props}
          dropdownClassName={cc([
            props.dropdownClassName || '',
            {
              'antdDropdown--mobile': isMobile()
            }
          ])}
        >
          {childrenArr}
        </AntSelect>
      </div>
    );
  }
);
